/** Various utilities for working with the `foundAfter` filter. */
import moment from 'moment';
import type { FilterByCategory, Category } from '../../../shared/issuesV2';

/** All the option types supported for the `firstFound` filter. */
export enum FoundAfterOption {
  Anytime = 'anytime',
  Last7Days = 'last7days',
  Last14Days = 'last14days',
  Last30Days = 'last30days',
}

/**
 * Converts the type of a filter object with query-style foundAfter values to
 * values for the store.  This lets us derive the filters on the store from the
 * filters in the shared query, only changing the fields that change.
 *
 * Specifically, this changes { foundAfter?: string } to { foundAfter?: FoundAfterOption }
 *
 * TODO: This should be combined with other conversions if there are more fields
 * that need to change.
 */
export type ConvertFoundAfterFilterForUser<C extends Category, T> =
  T extends FilterByCategory<C>
    ? T & { foundAfter?: FoundAfterOption } // The query has type `string` so this narrows it.
    : T;

export const isValidFoundAfterOption = (value: any): value is FoundAfterOption =>
  Object.values<any>(FoundAfterOption).includes(value);

/**
 * Converts a FoundAfterOption to a date.  The dates are calculated by creating
 * a date that is midnight of N days ago in your local timezone.
 */
export const dateForFoundAfterOption = (option: FoundAfterOption | undefined): string | undefined => {
  switch (option) {
    case 'last7days':
      return moment().local().subtract(7, 'days').startOf('day')
        .toISOString();
    case 'last14days':
      return moment().local().subtract(14, 'days').startOf('day')
        .toISOString();
    case 'last30days':
      return moment().local().subtract(30, 'days').startOf('day')
        .toISOString();
    default:
      return undefined;
  }
};
